import { useEffect } from "react";
import UrgencyBanner from "@/components/UrgencyBanner";
import Testimonials from "@/components/Testimonials";
import LeadForm from "@/components/LeadForm";
import WhatsAppButton from "@/components/WhatsAppButton";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { LanguageProvider, useLang } from "@/components/LanguageContext";
import { Clock, ShieldCheck, Users, Zap, Star, Phone, Mail, MapPin, Instagram, Facebook } from "lucide-react";

const translations = {
  es: {
    heroBadge: "+40 clientes satisfechos · Servicio Élite",
    heroTitle1: "¿Sabes qué se esconde",
    heroTitleHighlight: "realmente",
    heroTitle2: "en las esquinas de tu hogar?",
    heroSub1: 'Descubre el secreto de una limpieza nivel "Élite" que transforma espacios y cambia tu calidad de vida.',
    heroSub2: "Liberamos tu tiempo y garantizamos un ambiente impecable y saludable con nuestro personal certificado.",
    heroSub2Bold: "Tú relájate, nosotros nos encargamos.",
    heroCta: "🎯 ¡Quiero mi Presupuesto Gratis Ahora!",
    heroWa: "Hablar por WhatsApp",
    heroBadge1: "Sin compromiso",
    heroBadge2: "Respuesta en 2 horas",
    heroBadge3: "20% descuento primera limpieza",
    ownerLabel: "Ivis · Fundadora",
    statsLabel: ["Clientes Satisfechos", "Garantía de Satisfacción", "De Experiencia", "Valoración Media"],
    whyBadge: "¿Por Qué Elegirnos?",
    whyTitle: "No somos una empresa de limpieza más.",
    whyTitleHighlight: "Somos tu solución.",
    whySub: "Cada detalle importa. Por eso hemos diseñado un servicio que resuelve tus problemas reales, no solo limpia superficies.",
    benefits: [
      { title: "Tiempo para Ti", desc: "Recupera horas de tu día. Nosotros nos encargamos de todo para que tú te centres en lo que realmente importa." },
      { title: "Garantía de Satisfacción", desc: "Si no quedas 100% satisfecho, volvemos a limpiar sin coste adicional. Tu tranquilidad es nuestra prioridad." },
      { title: "Personal de Confianza", desc: "Todo nuestro equipo está certificado, con seguro incluido y verificado. Tu hogar u oficina en las mejores manos." },
      { title: "Tecnología de Punta", desc: "Usamos maquinaria profesional y productos ecológicos de última generación para resultados que se notan." },
    ],
    processBadge: "Así de Fácil",
    processTitle: "Tu limpieza élite en 3 pasos",
    steps: [
      { title: "Solicita tu Presupuesto", desc: "Rellena el formulario en 60 segundos. Sin compromiso, sin letra pequeña." },
      { title: "Confirmamos y Agendamos", desc: "Te llamamos en menos de 2 horas para confirmar detalles y fecha." },
      { title: "¡Disfruta tu Espacio!", desc: "Nuestro equipo llega puntual y transforma tu espacio. Tú solo relájate." },
    ],
    guaranteeBadge: "Garantía de Satisfacción 100%",
    guaranteeTitle: "Tu satisfacción o lo repetimos gratis",
    guaranteeText: "Si por cualquier motivo no estás completamente satisfecho con el resultado, nuestro equipo volverá a limpiar",
    guaranteeTextBold: "sin ningún coste adicional",
    guaranteeTextEnd: ". Así de seguros estamos de nuestro trabajo.",
    guaranteeCta: "Solicitar Ahora con Garantía →",
    locationBadge: "Nuestra Ubicación",
    locationTitle: "Encuéntranos en Houston, Texas",
    locationSub: "Servimos toda el área metropolitana de Houston y alrededores.",
    locationBtn: "Cómo llegar →",
    locationPhone: "Teléfono / WhatsApp",
    locationEmail: "Email",
    statsYears: "+2 años",
    footerServices: "Servicios",
    footerContact: "Contacto",
    footerServiceList: ["Limpieza Residencial – Deep Cleaning", "Limpieza Residencial – Regular", "Limpieza de Mudanza", "Organización del Hogar"],
    footerDesc: "Professional cleaning services in Houston, TX. Certified staff, total guarantee and cutting-edge technology. Serving the greater Houston area.",
    footerCta: "Solicitar Presupuesto Gratis",
    footerPrivacy: "Política de Privacidad",
    footerLegal: "Aviso Legal",
    footerCookies: "Cookies",
    footerCopy: "© 2025 IvisS Cleaning Company. All rights reserved.",
  },
  en: {
    heroBadge: "+40 satisfied clients · Elite Service",
    heroTitle1: "Do you know what's hiding",
    heroTitleHighlight: "really",
    heroTitle2: "in the corners of your home?",
    heroSub1: 'Discover the secret of an "Elite" level cleaning that transforms spaces and changes your quality of life.',
    heroSub2: "We free up your time and guarantee a spotless, healthy environment with our certified staff.",
    heroSub2Bold: "You relax, we take care of everything.",
    heroCta: "🎯 Get My Free Quote Now!",
    heroWa: "Chat on WhatsApp",
    heroBadge1: "No commitment",
    heroBadge2: "Response in 2 hours",
    heroBadge3: "20% off first cleaning",
    ownerLabel: "Ivis · Founder",
    statsLabel: ["Satisfied Clients", "Satisfaction Guarantee", "Years of Experience", "Average Rating"],
    whyBadge: "Why Choose Us?",
    whyTitle: "We're not just another cleaning company.",
    whyTitleHighlight: "We are your solution.",
    whySub: "Every detail matters. That's why we've designed a service that solves your real problems, not just cleans surfaces.",
    benefits: [
      { title: "Time for You", desc: "Reclaim hours of your day. We handle everything so you can focus on what really matters." },
      { title: "Satisfaction Guarantee", desc: "If you're not 100% satisfied, we'll clean again at no extra cost. Your peace of mind is our priority." },
      { title: "Trusted Staff", desc: "Our entire team is certified, insured, and verified. Your home or office in the best hands." },
      { title: "Cutting-Edge Technology", desc: "We use professional equipment and eco-friendly products for results you can see and feel." },
    ],
    processBadge: "That Easy",
    processTitle: "Your elite cleaning in 3 steps",
    steps: [
      { title: "Request Your Quote", desc: "Fill out the form in 60 seconds. No commitment, no fine print." },
      { title: "We Confirm & Schedule", desc: "We call you within 2 hours to confirm details and date." },
      { title: "Enjoy Your Space!", desc: "Our team arrives on time and transforms your space. Just relax." },
    ],
    guaranteeBadge: "100% Satisfaction Guarantee",
    guaranteeTitle: "Your satisfaction or we redo it for free",
    guaranteeText: "If for any reason you're not completely satisfied with the result, our team will come back and clean again",
    guaranteeTextBold: "at no additional cost",
    guaranteeTextEnd: ". That's how confident we are in our work.",
    guaranteeCta: "Request Now with Guarantee →",
    locationBadge: "Our Location",
    locationTitle: "Find Us in Houston, Texas",
    locationSub: "We serve the entire Houston metropolitan area and surroundings.",
    locationBtn: "Get Directions →",
    locationPhone: "Phone / WhatsApp",
    locationEmail: "Email",
    statsYears: "+2 years",
    footerServices: "Services",
    footerContact: "Contact",
    footerServiceList: ["Residential Cleaning – Deep Cleaning", "Residential Cleaning – Regular", "Move-In / Move-Out Cleaning", "Home Organization"],
    footerDesc: "Professional cleaning services in Houston, TX. Certified staff, total guarantee and cutting-edge technology. Serving the greater Houston area.",
    footerCta: "Request Free Quote",
    footerPrivacy: "Privacy Policy",
    footerLegal: "Legal Notice",
    footerCookies: "Cookies",
    footerCopy: "© 2025 IvisS Cleaning Company. All rights reserved.",
  },
};

const benefitIcons = [
  { icon: <Clock className="w-8 h-8 text-green-600" />, bg: "bg-green-50", border: "border-green-100" },
  { icon: <ShieldCheck className="w-8 h-8 text-green-700" />, bg: "bg-gray-50", border: "border-gray-100" },
  { icon: <Users className="w-8 h-8 text-green-600" />, bg: "bg-green-50", border: "border-green-100" },
  { icon: <Zap className="w-8 h-8 text-green-700" />, bg: "bg-gray-50", border: "border-gray-100" },
];

const stepEmojis = ["📋", "📞", "✨"];
const statsNums = (tx) => ["+40", "100%", tx.statsYears, "4.9/5"];

function LandingContent() {
  const { lang } = useLang();
  const tx = translations[lang];

  const scrollToForm = () => {
    document.getElementById("formulario")?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (document.querySelector('script[src="https://unpkg.com/@elevenlabs/convai-widget-embed"]')) return;
    const script = document.createElement("script");
    script.src = "https://unpkg.com/@elevenlabs/convai-widget-embed";
    script.async = true;
    script.type = "text/javascript";
    document.body.appendChild(script);
  }, []);

  return (
    <div className="min-h-screen font-sans bg-white">
      <UrgencyBanner />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1920&q=80')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-green-950/80 to-green-900/70" />

        {/* Language Switcher - top left */}
        <div className="absolute top-4 left-4 z-20">
          <LanguageSwitcher />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 w-full">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">

            {/* LEFT: Text content */}
            <div className="flex-1 text-white text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-green-400/40 rounded-full px-4 py-2 text-sm font-medium mb-6">
                <Star className="w-4 h-4 fill-green-400 text-green-400" />
                <span className="text-green-100">{tx.heroBadge}</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 drop-shadow-lg">
                {tx.heroTitle1}{" "}
                <span className="text-green-400">{tx.heroTitleHighlight}</span>{" "}
                {tx.heroTitle2}
              </h1>

              <p className="text-xl md:text-2xl text-green-100 font-light mb-4 leading-relaxed">
                {tx.heroSub1}
              </p>

              <p className="text-base md:text-lg text-green-200 mb-10">
                {tx.heroSub2}{" "}
                <strong className="text-white">{tx.heroSub2Bold}</strong>
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start items-center">
                <button
                  onClick={scrollToForm}
                  className="bg-green-500 hover:bg-green-400 text-white font-bold py-4 px-10 rounded-2xl text-lg shadow-2xl hover:shadow-green-500/40 hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  {tx.heroCta}
                </button>
                <a
                  href="https://wa.me/17867672593"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/20 border border-white/40 text-white font-semibold py-4 px-8 rounded-2xl text-base shadow-lg hover:scale-105 transition-all duration-200 flex items-center gap-2 backdrop-blur-sm"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-green-400"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                  {tx.heroWa}
                </a>
              </div>

              <div className="mt-8 flex flex-wrap justify-center md:justify-start gap-6 text-sm text-green-200">
                <span className="flex items-center gap-1">✅ {tx.heroBadge1}</span>
                <span className="flex items-center gap-1">✅ {tx.heroBadge2}</span>
                <span className="flex items-center gap-1">✅ {tx.heroBadge3}</span>
              </div>
            </div>

            {/* RIGHT: Owner photo */}
            <div className="flex-shrink-0 flex flex-col items-center gap-4">
              <div className="relative inline-block">
                <img
                  src="https://cdn.agentui.ai/cmnajuufy0nt2s51j16517109/1774720108222-e3d92ef7/ivis_2.jpeg"
                  alt="Ivis - Fundadora de IvisS Cleaning Company"
                  className="w-56 md:w-72 lg:w-80 rounded-2xl object-cover shadow-2xl border-2 border-green-400/40"
                />
                <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg whitespace-nowrap">{tx.ownerLabel}</span>
              </div>
            </div>

          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce text-green-300/70">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-gray-950 text-white py-8 px-4 border-b-4 border-green-500">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {statsNums(tx).map((num, i) => (
            <div key={i}>
              <p className="text-3xl font-extrabold text-green-400">{num}</p>
              <p className="text-sm text-gray-400 mt-1">{tx.statsLabel[i]}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-widest">{tx.whyBadge}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">
              {tx.whyTitle}{" "}
              <span className="text-green-600">{tx.whyTitleHighlight}</span>
            </h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">{tx.whySub}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {tx.benefits.map((b, i) => (
              <div key={i} className={`${benefitIcons[i].bg} rounded-2xl p-6 flex flex-col gap-4 hover:shadow-xl transition-all duration-200 border ${benefitIcons[i].border} hover:border-green-300 group`}>
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                  {benefitIcons[i].icon}
                </div>
                <h3 className="font-bold text-gray-900 text-lg">{b.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-gray-950 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-green-400 font-semibold text-sm uppercase tracking-widest">{tx.processBadge}</span>
          <h2 className="text-3xl font-bold text-white mt-2 mb-10">{tx.processTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tx.steps.map((p, i) => (
              <div key={i} className="bg-gray-900 border border-gray-800 hover:border-green-500 rounded-2xl p-6 shadow-sm relative transition-all duration-200 group">
                <span className="absolute -top-3 -left-3 bg-green-500 text-white text-xs font-bold w-8 h-8 rounded-full flex items-center justify-center shadow-lg">0{i + 1}</span>
                <div className="text-4xl mb-4">{stepEmojis[i]}</div>
                <h3 className="font-bold text-white text-lg mb-2">{p.title}</h3>
                <p className="text-gray-400 text-sm">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* LEAD FORM */}
      <LeadForm />

      {/* GUARANTEE */}
      <section className="bg-gradient-to-br from-gray-950 via-green-950 to-black py-16 px-4">
        <div className="max-w-3xl mx-auto text-center text-white">
          <div className="inline-flex flex-col items-center gap-4 bg-white/5 backdrop-blur-sm border border-green-500/30 rounded-3xl px-10 py-10">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-xl">
              <ShieldCheck className="w-12 h-12 text-green-600" />
            </div>
            <div className="bg-green-500 text-white font-extrabold text-sm uppercase tracking-widest px-4 py-1 rounded-full">
              {tx.guaranteeBadge}
            </div>
            <h2 className="text-2xl md:text-3xl font-bold">{tx.guaranteeTitle}</h2>
            <p className="text-gray-300 max-w-lg text-base leading-relaxed">
              {tx.guaranteeText}{" "}
              <strong className="text-white">{tx.guaranteeTextBold}</strong>
              {tx.guaranteeTextEnd}
            </p>
            <button
              onClick={() => document.getElementById("formulario")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-green-500 hover:bg-green-400 text-white font-bold py-3 px-8 rounded-xl text-base transition-all hover:scale-105 shadow-lg shadow-green-900/50 mt-2"
            >
              {tx.guaranteeCta}
            </button>
          </div>
        </div>
      </section>

      {/* LOCATION / GOOGLE MAPS */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-widest">{tx.locationBadge}</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">{tx.locationTitle}</h2>
            <p className="text-gray-500 mt-2">{tx.locationSub}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="bg-gray-950 rounded-2xl p-6 flex flex-col gap-5 border border-gray-800">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-white">IvisS Cleaning Company</p>
                  <p className="text-gray-400 text-sm mt-1">14150 Wunderlich Dr</p>
                  <p className="text-gray-400 text-sm">Houston, Texas 77069</p>
                  <p className="text-gray-400 text-sm">United States</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-900/50 rounded-xl flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{tx.locationPhone}</p>
                  <a href="tel:+17867672593" className="font-bold text-white hover:text-green-400 transition-colors">+1 (786) 767-2593</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-900/50 rounded-xl flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{tx.locationEmail}</p>
                  <a href="mailto:Ivisscleaning@gmail.com" className="font-bold text-white hover:text-green-400 transition-colors text-sm">Ivisscleaning@gmail.com</a>
                </div>
              </div>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=14150+Wunderlich+Dr,+Houston,+TX+77069"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-400 text-white font-semibold py-3 px-5 rounded-xl text-sm text-center transition-all hover:scale-105 flex items-center justify-center gap-2"
              >
                <MapPin className="w-4 h-4" /> {tx.locationBtn}
              </a>
            </div>
            <div className="lg:col-span-2 rounded-2xl overflow-hidden shadow-lg border border-gray-200 h-80 lg:h-96">
              <iframe
                title="IvisS Cleaning Company Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3455.123456789!2d-95.5135!3d29.9902!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640d0b0b0b0b0b0%3A0x0!2s14150+Wunderlich+Dr%2C+Houston%2C+TX+77069!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-950 text-gray-300 py-12 px-4 border-t border-gray-800">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">✦</span>
              </div>
              <span className="text-white font-bold text-xl">IvisS Cleaning Company</span>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">{tx.footerDesc}</p>
            <div className="flex gap-3 mt-5">
              {[
                { icon: <Instagram className="w-4 h-4" />, href: "https://www.instagram.com/ivisscleaningcompany" },
                { icon: <Facebook className="w-4 h-4" />, href: "https://www.facebook.com/ivisScleangcompany" },
                { icon: <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z"/></svg>, href: "https://www.tiktok.com/@ivisnely" },
              ].map((s, i) => (
                <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 hover:bg-green-600 rounded-lg flex items-center justify-center transition-colors">
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">{tx.footerServices}</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              {tx.footerServiceList.map((s, i) => (
                <li key={i} className="hover:text-green-400 cursor-pointer transition-colors">→ {s}</li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">{tx.footerContact}</h4>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-green-400" /> +1 (786) 767-2593</li>
              <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-green-400" /> Ivisscleaning@gmail.com</li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-green-400 mt-0.5 shrink-0" /> 14150 Wunderlich Dr, Houston, TX 77069</li>
            </ul>
            <button
              onClick={() => document.getElementById("formulario")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-6 bg-green-500 hover:bg-green-400 text-white font-semibold py-2 px-6 rounded-xl text-sm transition-all"
            >
              {tx.footerCta}
            </button>
          </div>
        </div>

        <div className="max-w-6xl mx-auto border-t border-gray-800 mt-10 pt-6 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-gray-600">
          <p>{tx.footerCopy}</p>
          <div className="flex gap-4">
            <span className="hover:text-green-400 cursor-pointer transition-colors">{tx.footerPrivacy}</span>
            <span className="hover:text-green-400 cursor-pointer transition-colors">{tx.footerLegal}</span>
            <span className="hover:text-green-400 cursor-pointer transition-colors">{tx.footerCookies}</span>
          </div>
        </div>
      </footer>

      <WhatsAppButton />

      {/* ELEVENLABS CHATBOT */}
      <div style={{ position: 'fixed', top: '16px', right: '16px', zIndex: 9999 }}>
        <elevenlabs-convai agent-id="agent_2001kmv8hbcme2rtzvdfmgee83je"></elevenlabs-convai>
      </div>
    </div>
  );
}

export default function LandingPage() {
  return <LandingContent />;
}